import { BarChart2, ClipboardList, FileText, Package, Repeat, Tag, Warehouse } from "lucide-react";
import React from "react";
import { NavLink, useParams } from "react-router-dom";
import { Card, CardContent } from "randmarcomps";
import DownloadReport from "../components/manufacturer/DownloadReport";
import DownloadSalesReport from "../components/manufacturer/DownloadSalesReport";
import { useUser, IUserHookResult } from "../hooks/useUser";
import Path from "../routes/Path";

interface ReportItem {
  title: string;
  description: string;
  Icon: React.ElementType;
  isSalesReport?: boolean;
  buttonTitle?: string;
  type?: string;
  helperText?: string;
  badge?: string;
  isDisabled?: boolean;
  disabledReason?: string;
}

interface QuickLink {
  title: string;
  description: string;
  icon: React.ElementType;
  accentClass: string;
  external?: boolean;
  href?: string;
  getHref?: (applicationId: string) => string;
}

type User = IUserHookResult["user"];

const quickLinks: QuickLink[] = [
  {
    title: "Warehouse Information",
    description: "Contact details, cut-off reminders, and service areas in one place.",
    icon: Warehouse,
    accentClass: "text-blue-600 bg-blue-50",
    getHref: (applicationId) => Path.warehouseInformation(applicationId),
  },
];

const pickupCities = ["Vancouver", "Edmonton", "Toronto", "Montreal"] as const;
type PickupCity = (typeof pickupCities)[number];

const pickupSchedule: Array<{ courier: string; times: Record<PickupCity, string> }> = [
  {
    courier: "Canpar",
    times: {
      Vancouver: "14:30",
      Edmonton: "16:00",
      Toronto: "16:30",
      Montreal: "18:30",
    },
  },
  {
    courier: "Canada Post",
    times: {
      Vancouver: "14:20",
      Edmonton: "16:00",
      Toronto: "15:30",
      Montreal: "16:00",
    },
  },
  {
    courier: "FedEx",
    times: {
      Vancouver: "14:20",
      Edmonton: "14:30",
      Toronto: "15:30",
      Montreal: "17:00",
    },
  },
  {
    courier: "Purolator",
    times: {
      Vancouver: "16:00",
      Edmonton: "15:00",
      Toronto: "16:30",
      Montreal: "18:30",
    },
  },
  {
    courier: "UPS",
    times: {
      Vancouver: "14:20",
      Edmonton: "16:00",
      Toronto: "17:30",
      Montreal: "15:30",
    },
  },
];

const panelClassName = "rounded-[var(--surface-radius-xl)] border border-[var(--ops-border)] bg-[var(--ops-card)]";

const ReportSection: React.FC<{
  title: string;
  reports: ReportItem[];
  user: User;
}> = ({ title, reports, user }) => {
  const headingId = React.useId();

  return (
    <section aria-labelledby={headingId} className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h2 id={headingId} className="text-lg font-semibold text-slate-900 tracking-tight">
          {title}
        </h2>
        <span className="text-xs font-semibold uppercase tracking-wide text-[var(--ops-text-muted)]">Reports</span>
      </div>
      <Card className={`${panelClassName} overflow-hidden`} style={{ boxShadow: "var(--shadow-card)" }}>
        <CardContent className="p-0">
          <ul role="list" className="divide-y divide-[var(--ops-border)]">
            {reports.map((report, index) => (
              <ReportRow
                key={report.title}
                index={index}
                report={report}
                sectionId={headingId}
                user={user}
              />
            ))}
          </ul>
        </CardContent>
      </Card>
    </section>
  );
};

const orderAndSalesReports: ReportItem[] = [
  {
    title: "Open Orders Report",
    description: "The Open Orders Report provides a snapshot of active orders, aiding your business in efficiently tracking and fulfilling customer demands.",
    buttonTitle: "Download Open Orders Report",
    type: "OpenOrders",
    Icon: FileText,
    helperText: "Delivered via email in minutes",
  },
  {
    isSalesReport: true,
    title: "Sales Report",
    description: "Summary of your company's sales activity over a certain period, detailing total sales and helping identifying trends, successes, and areas needing improvement.",
    Icon: BarChart2,
    buttonTitle: "Generate Sales Report",
    helperText: "Filter by reseller or timeframe",
  },
  {
    title: "Claim Report",
    description: "Summary of your company's opportunities (bids) activity and sales over a certain period, detailing total sales for rebates provided to resellers through our Opportunities program.",
    buttonTitle: "Claim Report",
    type: "Claim",
    Icon: Tag,
    helperText: "Best for quarterly rebate reviews",
  },
];

const purchaseOrdersReports: ReportItem[] = [
  {
    title: "Open Purchase Orders Report",
    description: "The Open Purchase Orders Report provides a snapshot of Randmar's active orders, aiding your business in efficiently tracking and fulfilling Randmar's inventory demands.",
    buttonTitle: "Open POs Report",
    type: "PurchasedOrders",
    Icon: ClipboardList,
    helperText: "Pairs well with restocking workflows",
  },
  {
    title: "Reordering Report",
    description: "The Reordering Report provides a snapshot all part numbers auto-reordering statuses. Aiding your understanding of what will be automatically reordered by our system or not once the inventory reaches low numbers.",
    buttonTitle: "Reorder Report",
    type: "Reorder",
    Icon: Repeat,
    helperText: "Highlight SKUs below thresholds",
    badge: "Beta",
  },
];

const inventoryReports: ReportItem[] = [
  {
    title: "Inventory Report",
    description: "Detailed document outlining the quantity, type, and value of products Randmar has on hand. It aids in managing stock levels, identifying shortages or surpluses, and informing purchasing decisions.",
    buttonTitle: "Inventory Report",
    type: "Inventory",
    Icon: Warehouse,
    helperText: "Snapshot of on-hand quantities",
  },
  {
    title: "All Products Report",
    description: "Detailed document outlining all your products properties. It provides a comprehensive overview of essential information and insights that help you make informed decisions.",
    buttonTitle: "All Products Report",
    type: "Products",
    Icon: Package,
    helperText: "Export-ready master catalog",
  },
];

const QuickLinks: React.FC<{ applicationId: string }> = ({ applicationId }) => {
  const gridClassName = quickLinks.length > 1 ? "grid grid-cols-1 gap-4 lg:grid-cols-2" : "grid grid-cols-1 gap-4";

  return (
    <nav aria-label="Quick links" className={gridClassName}>
      {quickLinks.map((link) => {
        const Icon = link.icon;
        const destination = link.external ? link.href ?? "#" : link.getHref ? link.getHref(applicationId) : "#";
        const commonClasses = "group block focus-visible:outline-none";
        const card = (
          <Card
            className={`${panelClassName} h-full bg-gradient-to-br from-white via-[var(--ops-card)] to-[var(--ops-card-muted)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[var(--shadow-card-hover)] group-focus-visible:shadow-[var(--shadow-card-hover)]`}
            style={{ boxShadow: "var(--shadow-card)", transitionTimingFunction: "var(--ease-out)" }}
          >
            <CardContent className="flex h-full flex-col gap-4 p-6 text-left">
              <span className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl ${link.accentClass}`}>
                <Icon className="h-5 w-5" aria-hidden="true" />
              </span>
              <div className="space-y-1">
                <h2 className="text-xl font-semibold text-slate-900">{link.title}</h2>
                <p className="text-sm text-[var(--ops-text-muted)]">{link.description}</p>
              </div>
              {link.external && (
                <span className="mt-auto text-xs font-semibold uppercase tracking-wide text-[var(--ops-text-muted)]">
                  Opens in new tab
                </span>
              )}
            </CardContent>
          </Card>
        );

        return link.external ? (
          <a
            key={link.title}
            href={destination}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${link.title} (opens in a new tab)`}
            className={commonClasses}
          >
            {card}
          </a>
        ) : (
          <NavLink key={link.title} to={destination} aria-label={link.title} className={commonClasses}>
            {card}
          </NavLink>
        );
      })}
    </nav>
  );
};

const helperTextDefault = "Delivered via email";

const ReportRow: React.FC<{
  report: ReportItem;
  sectionId: string;
  index: number;
  user: User;
}> = ({ report, sectionId, index, user }) => {
  const descriptionId = `${sectionId}-description-${index}`;
  const disabled = report.isDisabled ?? false;
  const actionLabel = report.buttonTitle ?? `Download ${report.title}`;
  const helperCopy = disabled ? report.disabledReason ?? "Temporarily unavailable" : report.helperText ?? helperTextDefault;
  const actionClasses =
    "border-transparent bg-[var(--ops-accent)] text-white shadow-[0_10px_30px_rgba(105,50,198,0.25)] hover:bg-[var(--ops-accent)]/90";
  const ActionIcon = report.Icon ?? FileText;

  const renderAction = () => {
    if (report.isSalesReport) {
      return (
        <DownloadSalesReport
          buttonTitle={actionLabel}
          triggerClassName={actionClasses}
          triggerAriaLabel={actionLabel}
          triggerDescribedBy={descriptionId}
          disabled={disabled}
        >
          <ActionIcon className="h-4 w-4" aria-hidden="true" />
        </DownloadSalesReport>
      );
    }

    if (!report.type) {
      return null;
    }

    return (
      <DownloadReport
        user={user}
        buttonTitle={actionLabel}
        type={report.type}
        triggerClassName={actionClasses}
        triggerAriaLabel={actionLabel}
        triggerDescribedBy={descriptionId}
        disabled={disabled}
      >
        <ActionIcon className="h-4 w-4" aria-hidden="true" />
      </DownloadReport>
    );
  };

  return (
    <li
      className="group flex flex-col gap-4 p-6 transition-all duration-300 hover:bg-[var(--ops-accent-muted)]/40 focus-within:bg-[var(--ops-accent-muted)]/60 data-[disabled=true]:opacity-60 md:flex-row md:items-center"
      style={{ transitionTimingFunction: "var(--ease-out)" }}
      data-disabled={disabled}
      aria-disabled={disabled}
    >
      <div className="flex flex-1 items-start gap-4">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--ops-accent-muted)] text-[var(--ops-accent)]">
          <report.Icon className="h-5 w-5" aria-hidden="true" />
        </span>
        <div className="space-y-1 text-left">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-semibold text-slate-900">{report.title}</h3>
            {report.badge && (
              <span className="rounded-full bg-[var(--ops-accent-muted)] px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-[var(--ops-accent)]">
                {report.badge}
              </span>
            )}
          </div>
          <p id={descriptionId} className="text-sm text-[var(--ops-text-muted)]">
            {report.description}
          </p>
        </div>
      </div>
      <div className="flex flex-col gap-2 text-left md:min-w-[200px] md:text-right">
        {renderAction()}
        <span className="text-xs text-[var(--ops-text-muted)]">{helperCopy}</span>
      </div>
    </li>
  );
};

const CourierPickupsBase: React.FC = () => {
  const headingId = React.useId();

  return (
    <section aria-labelledby={headingId} className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h2 id={headingId} className="text-lg font-semibold text-slate-900 tracking-tight">
          Courier Pick-ups
        </h2>
        <span className="text-xs font-semibold uppercase tracking-wide text-[var(--ops-text-muted)]">Logistics</span>
      </div>
      <Card className={`${panelClassName} overflow-hidden`} style={{ boxShadow: "var(--shadow-card)" }}>
        <CardContent className="space-y-6 p-6">
          <div
            className="inline-flex items-center gap-2 rounded-full bg-[var(--ops-accent-muted)] px-4 py-1 text-xs font-semibold uppercase tracking-wide text-[var(--ops-accent)]"
            role="status"
            aria-live="polite"
          >
            <span className="h-2 w-2 rounded-full bg-[var(--ops-accent)]" aria-hidden="true" />
            In local time
          </div>

          <div className="grid gap-4 md:hidden">
            {pickupSchedule.map((schedule) => (
              <article
                key={schedule.courier}
                className="rounded-2xl border border-[var(--ops-border)] bg-[var(--ops-surface)] p-4"
                aria-label={`${schedule.courier} pick-up times`}
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-slate-900">{schedule.courier}</h3>
                  <span className="text-xs uppercase text-[var(--ops-text-muted)]">Daily</span>
                </div>
                <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  {pickupCities.map((city) => (
                    <div key={`${schedule.courier}-${city}`} className="rounded-xl bg-white/70 p-3">
                      <dt className="text-[var(--ops-text-muted)]">{city}</dt>
                      <dd className="text-base font-semibold text-slate-900">{schedule.times[city]}</dd>
                    </div>
                  ))}
                </dl>
              </article>
            ))}
          </div>

          <div className="hidden md:block">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm" aria-describedby={`${headingId}-caption`}>
                <caption id={`${headingId}-caption`} className="sr-only">
                  Courier pick-up schedule by city
                </caption>
                <thead className="bg-[var(--ops-accent-muted)] text-slate-900">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">
                      Courier
                    </th>
                    {pickupCities.map((city) => (
                      <th
                        scope="col"
                        key={`${headingId}-${city}`}
                        className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide"
                      >
                        {city}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pickupSchedule.map((schedule, rowIndex) => (
                    <tr
                      key={`${schedule.courier}-${rowIndex}`}
                      className="odd:bg-white even:bg-[var(--ops-surface)] transition-colors hover:bg-[var(--ops-accent-muted)]/30"
                    >
                      <th scope="row" className="px-4 py-3 text-left font-semibold text-slate-900">
                        {schedule.courier}
                      </th>
                      {pickupCities.map((city) => (
                        <td key={`${schedule.courier}-${city}`} className="px-4 py-3 text-center text-slate-900">
                          {schedule.times[city]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </CardContent>
      </Card>
    </section>
  );
};

const CourierPickups = React.memo(CourierPickupsBase);
CourierPickups.displayName = "CourierPickups";

const SiteMap: React.FC = () => {
  const { user } = useUser();
  const { applicationId } = useParams<{ applicationId: string }>();

  React.useEffect(() => {
    const previousTitle = document.title;
    if (!previousTitle.toLowerCase().includes("site map")) {
      document.title = `Site Map | ${previousTitle}`;
    }

    return () => {
      document.title = previousTitle;
    };
  }, []);

  if (!applicationId) {
    return (
      <section
        role="status"
        aria-live="polite"
        aria-busy="true"
        className={`${panelClassName} animate-pulse p-6 text-sm text-[var(--ops-text-muted)]`}
        style={{ boxShadow: "var(--shadow-card)" }}
      >
        Loading site map data...
      </section>
    );
  }

  return (
    <main className="space-y-8" aria-live="polite">
      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="flex w-full flex-col gap-6 lg:w-7/12">
          <ReportSection title="Orders & Sales" reports={orderAndSalesReports} user={user} />
          <ReportSection title="Purchase Orders" reports={purchaseOrdersReports} user={user} />
          <ReportSection title="Inventory" reports={inventoryReports} user={user} />
        </div>
        <div className="flex w-full flex-col gap-6 lg:w-5/12">
          <CourierPickups />
          <QuickLinks applicationId={applicationId} />
        </div>
      </div>
    </main>
  );
};

export default SiteMap;
